import React from 'react';

import styles from './OfflineMode.module.scss';
import { REACH, REACH_BUCKET_IDS } from './copy';
import type { ReachBucketId } from './copy';
import { SkeletonBlock, SkeletonRegion } from './Skeleton';
import { anyChannelAvailable, boundFor, formatCount, notMeasuredLimit } from './reachReading';
import { useServerDiagnostics } from './serverDiagnostics';
import type {
  OfflineModeDirection,
  OfflineModeReach,
  OfflineModeReachChannel,
  OfflineModeReachError,
  OfflineModeReachQuality,
} from 'redux-stores/offlineMode';

export type ReachPanelProps = {
  direction: OfflineModeDirection;
  status: 'idle' | 'gathering' | 'ready' | 'error';
  reach: OfflineModeReach | null;
  error: OfflineModeReachError | null;
  deadlineElapsed: boolean;
};

const limitDigest = (quality: OfflineModeReachQuality): string =>
  quality.limits
    .map((limit) => [limit.code, limit.ticket, limit.effect, limit.text].filter(Boolean).join(' '))
    .join(' | ');

const channelDigest = (channels: OfflineModeReachChannel[]): string =>
  channels
    .map((channel) => [channel.channel, channel.basis, channel.text].filter(Boolean).join(' '))
    .join(' | ');

const channelName = (channel: OfflineModeReachChannel): string =>
  REACH.channels.names[channel.channel] || REACH.channels.unknownName;

function ReachFigure({
  bucket,
  value,
  quality,
}: {
  bucket: ReachBucketId;
  value: number;
  quality: OfflineModeReachQuality;
}) {
  const { label } = REACH.buckets[bucket];

  if (notMeasuredLimit(quality, bucket)) {
    return (
      <div className={styles.reachFigure} data-test-id={`offlineModeReachFigure-${bucket}`}>
        <span
          className={styles.reachFigureUnmeasured}
          data-test-id={`offlineModeReachUnmeasured-${bucket}`}
        >
          {REACH.notMeasured}
        </span>
        <span className={styles.reachFigureLabel}>{label}</span>
      </div>
    );
  }

  const bound = boundFor(quality, bucket);
  const boundWord = REACH.bound[bound];
  const formatted = formatCount(value);

  return (
    <div className={styles.reachFigure} data-test-id={`offlineModeReachFigure-${bucket}`}>
      <span
        aria-label={`${boundWord ? `${boundWord} ` : ''}${formatted} people. ${label}.`}
        className={styles.reachFigureValue}
        data-test-id={`offlineModeReachValue-${bucket}`}
        data-bound={bound}
      >
        {boundWord ? (
          <span aria-hidden="true" className={styles.reachFigureBound}>
            {boundWord}
          </span>
        ) : null}
        <span aria-hidden="true" className={styles.reachFigureNumber}>
          {formatted}
        </span>
      </span>
      <span className={styles.reachFigureLabel}>{label}</span>
    </div>
  );
}

function ReachChannelsAvailable({ channels }: { channels: OfflineModeReachChannel[] }) {
  if (!anyChannelAvailable(channels)) return null;

  const available = channels.filter((channel) => channel.available).map(channelName);

  return (
    <p className={styles.reachChannelsLede} data-test-id="offlineModeReachChannels">
      {`${REACH.channels.availableNow}: ${available.join(', ')}`}
    </p>
  );
}

function ReachDegraded({
  heading,
  body,
  testId,
}: {
  heading: string;
  body?: string;
  testId: string;
}) {
  return (
    <div className={styles.reachDegraded} data-test-id={testId} role="note">
      <h4 className={styles.reachDegradedHeading}>{heading}</h4>
      {body ? <p className={styles.reachDegradedBody}>{body}</p> : null}
      <p className={styles.reachDegradedConfirm} data-test-id="offlineModeReachConfirmAvailable">
        {REACH.confirmStillAvailable}
      </p>
    </div>
  );
}

function ReachGathering() {
  return (
    <SkeletonRegion label={REACH.gathering.label} testId="offlineModeReachSkeleton">
      <div className={styles.reachFigureRow}>
        {REACH_BUCKET_IDS.map((bucket) => (
          <div className={styles.reachFigure} key={bucket}>
            <SkeletonBlock height="26px" width="72%" />
            <SkeletonBlock height="12px" width="55%" />
            <SkeletonBlock height="10px" width="88%" />
          </div>
        ))}
      </div>
      <SkeletonBlock height="10px" width="70%" />
      <p className={styles.reachGatheringNote}>{REACH.gathering.note}</p>
    </SkeletonRegion>
  );
}

export default function ReachPanel({
  direction,
  status,
  reach,
  error,
  deadlineElapsed,
}: ReachPanelProps) {
  useServerDiagnostics('reach', {
    errorCode: error?.code,
    serverMessage: error?.serverMessage,
    unavailableCode: reach?.unavailableCode,
    unavailableReason: reach?.unavailableReason,
    displayCaveat: reach?.quality.displayCaveat,
    safeDirection: reach?.quality.safeDirection,
    countsLabel: reach?.counts?.label,
    limits: reach ? limitDigest(reach.quality) : undefined,
    channels: reach ? channelDigest(reach.channels) : undefined,
  });

  const heading = (
    <h3 className={styles.reachHeading} data-test-id="offlineModeReachHeading">
      {REACH.heading(direction)}
    </h3>
  );

  const wrap = (children: React.ReactNode) => (
    <section className={styles.reach} data-test-id="offlineModeReach">
      {heading}
      {children}
    </section>
  );

  if (status !== 'ready' && status !== 'error' && deadlineElapsed) {
    return wrap(
      <ReachDegraded
        body={REACH.deadline.body}
        heading={REACH.deadline.heading}
        testId="offlineModeReachDeadline"
      />
    );
  }

  if (status === 'idle' || status === 'gathering') return wrap(<ReachGathering />);

  if (status === 'error' || !reach) {
    const code = error?.code ?? 'unusable';
    return wrap(
      <ReachDegraded
        body={REACH.failure[code] ?? REACH.failure.unusable}
        heading={REACH.deadline.heading}
        testId="offlineModeReachError"
      />
    );
  }

  if (!reach.measured || !reach.counts) {
    return wrap(
      <>
        <ReachDegraded heading={REACH.unmeasurable.heading} testId="offlineModeReachUnmeasurable" />
        <ReachChannelsAvailable channels={reach.channels} />
      </>
    );
  }

  const { counts, quality } = reach;

  const value: Record<ReachBucketId, number> = {
    reachable_now: counts.reachableNow,
    reachable_on_rejoin: counts.reachableOnRejoin,
    not_reachable: counts.notReachable,
  };

  return wrap(
    <>
      {deadlineElapsed ? (
        <p className={styles.reachLate} data-test-id="offlineModeReachLate" role="note">
          {REACH.lateArrival}
        </p>
      ) : null}

      <div className={styles.reachFigureRow}>
        {REACH_BUCKET_IDS.map((bucket) => (
          <ReachFigure bucket={bucket} key={bucket} quality={quality} value={value[bucket]} />
        ))}
      </div>

      <ReachChannelsAvailable channels={reach.channels} />
    </>
  );
}
